// src/components/Pagination.js
import React from 'react';

/**
 * Reusable pagination control. Styling lives in App.css (.pg-*).
 *
 * Props:
 *   page             current 1-based page
 *   pageSize         rows per page
 *   total            total number of rows
 *   onPageChange     (nextPage) => void
 *   onPageSizeChange (nextSize) => void   (optional — hides the size picker if omitted)
 *   pageSizeOptions  number[]  (default [5, 10, 20, 50])
 */
export default function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 20, 50],
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;

  if (total === 0) return null;

  return (
    <div className="pg">
      <span className="pg-info">
        Showing {start + 1}–{Math.min(start + pageSize, total)} of {total}
      </span>

      <div className="pg-pages">
        <button className="pg-btn" disabled={safePage <= 1} onClick={() => onPageChange(safePage - 1)}>‹ Prev</button>
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          const p = Math.max(1, Math.min(safePage - 2, totalPages - 4)) + i;
          return (
            <button
              key={p}
              className={`pg-btn${p === safePage ? ' is-active' : ''}`}
              onClick={() => onPageChange(p)}
            >
              {p}
            </button>
          );
        })}
        <button className="pg-btn" disabled={safePage >= totalPages} onClick={() => onPageChange(safePage + 1)}>Next ›</button>
      </div>

      {onPageSizeChange && (
        <select className="pg-btn pg-size" value={pageSize} onChange={e => onPageSizeChange(+e.target.value)}>
          {pageSizeOptions.map(n => <option key={n} value={n}>{n} / page</option>)}
        </select>
      )}
    </div>
  );
}
